export const GOOGLE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID || '706720560213-1f3dmo50amk180u2a7o6qcuqh2hm435i.apps.googleusercontent.com';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

export interface GoogleCredentialResponse {
  credential?: string;
  select_by?: string;
}

interface GoogleButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  logo_alignment?: 'left' | 'center';
  width?: number;
}

interface GoogleIdApi {
  initialize: (config: Record<string, unknown>) => void;
  renderButton: (parent: HTMLElement, options: GoogleButtonOptions) => void;
  prompt: () => void;
  cancel: () => void;
  disableAutoSelect: () => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdApi } };
  }
}

let loading: Promise<GoogleIdApi> | null = null;

export function loadGoogleIdentity(): Promise<GoogleIdApi> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  loading ??= new Promise<GoogleIdApi>((resolve, reject) => {
    let blockedByPolicy = false;
    const onViolation = (event: SecurityPolicyViolationEvent) => {
      if (event.blockedURI.includes('accounts.google.com')) blockedByPolicy = true;
    };
    document.addEventListener('securitypolicyviolation', onViolation);
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      document.removeEventListener('securitypolicyviolation', onViolation);
      const api = window.google?.accounts?.id;
      if (api) resolve(api);
      else reject(new Error('Google Identity Services did not initialise.'));
    };
    script.onerror = () => window.setTimeout(() => {
      document.removeEventListener('securitypolicyviolation', onViolation);
      script.remove();
      reject(
        new Error(
          blockedByPolicy
            ? 'This server’s security policy blocked Google sign-in. Restart the API so it serves the updated policy.'
            : 'Google sign-in could not load. Check your connection, or allow accounts.google.com in your ad or privacy blocker.'
        )
      );
    }, 60);
    document.head.appendChild(script);
  });
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

export function randomNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function forgetGoogleAccount(): void {
  try {
    window.google?.accounts?.id?.cancel();
    window.google?.accounts?.id?.disableAutoSelect();
  } catch {
    return;
  }
}
